import { useEffect, useState } from "react";
import { ArrowLeft, Wand2, Gamepad2, ChevronRight, BarChart3, Library } from "lucide-react";
import PipGamesZone from "@/components/games/PipGamesZone";
import IntentDashboard from "@/components/cockpit/IntentDashboard";
import { useSession } from "@/context/SessionContext";

// Misteck Play hub. Mist Games (Ball Snipe + Mist Miner) + Mist Creations.

export default function MisteckPlayHub({ onBack, onRouteHub }) {
  const [games, setGames] = useState(false);
  const [gamesInitial, setGamesInitial] = useState(null);   // deep-link: 'snipe' | 'miner'
  const [intentOpen, setIntentOpen] = useState(false);
  const { user } = useSession();
  const isFounder = !!user && (user.role === "founder" || user.handle === "johnnywalker" || user.email === "djohnm1972@gmail.com");

  useEffect(() => {
    window.dispatchEvent(new CustomEvent("mist:pip-hide", { detail: { active: true } }));
    return () => window.dispatchEvent(new CustomEvent("mist:pip-hide", { detail: { active: false } }));
  }, []);

  // Deep-links: ?play=snipe | ?play=miner jump straight into that game.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("play");
    if (p === "snipe" || p === "miner") { setGamesInitial(p); setGames(true); }
  }, []);

  if (games) return <PipGamesZone onBack={() => { setGames(false); setGamesInitial(null); }} initialOriginal={gamesInitial} />;
  if (intentOpen) return <IntentDashboard onBack={() => setIntentOpen(false)} />;

  return (
    <div className="absolute inset-0 z-30 flex flex-col bg-slate-950" data-testid="misteck-play-hub">
      <div className="flex items-center gap-2 border-b-2 border-white/70 px-4 py-3">
        <button onClick={onBack} data-testid="play-hub-back" className="grid h-9 w-9 place-items-center rounded-xl border-2 border-white bg-white/5 active:scale-95">
          <ArrowLeft size={16} className="text-white" />
        </button>
        <div className="leading-tight">
          <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-violet-300">Misteck Play</p>
          <h2 className="font-serif text-xl text-white">Creative & Games</h2>
        </div>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto no-scrollbar p-4" data-testid="play-hub-tiles">
        {/* Featured — Mist Games (hosts Ball Snipe + Mist Miner) */}
        <button
          onClick={() => setGames(true)}
          data-testid="play-tile-games"
          className="relative flex min-h-[26vh] w-full flex-col justify-between overflow-hidden rounded-[26px] border-2 border-black bg-[#4f46e5] p-4 text-left shadow-[6px_6px_0px_#000] transition-transform active:translate-x-[3px] active:translate-y-[3px] active:shadow-none"
        >
          <div className="flex items-center justify-between">
            <span className="grid h-12 w-12 place-items-center rounded-xl border-2 border-white/50 bg-white/15">
              <Gamepad2 size={22} className="text-white" />
            </span>
            <span className="rounded-full border-2 border-black bg-black px-2 py-0.5 font-mono text-[8px] font-black uppercase tracking-widest text-[#a78bfa]">2 games</span>
          </div>
          <div>
            <p className="font-serif text-3xl leading-none text-white">Mist Games</p>
            <p className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.22em] text-indigo-100/80">Ball Snipe · Mist Miner — earn gems → unlock tools</p>
          </div>
        </button>

        {isFounder && (
          <button
            onClick={() => setIntentOpen(true)}
            data-testid="play-tile-intent"
            className="w-full flex items-center gap-2.5 rounded-2xl border-2 border-black bg-[#FFCC00] px-4 py-3 text-left shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-transform"
          >
            <BarChart3 size={16} className="text-black" />
            <span className="flex-1 font-mono text-[11px] font-black uppercase tracking-widest text-black">Intent Dashboard</span>
            <ChevronRight size={15} className="text-black" />
          </button>
        )}

        {/* Mist Creations → studio tools */}
        <button
          onClick={() => { onRouteHub?.("imagery_hub"); onBack?.(); }}
          data-testid="play-tile-creations"
          className="relative flex min-h-[30vh] w-full flex-col justify-between overflow-hidden rounded-[26px] border-2 border-black bg-[#0d9488] p-4 text-left shadow-[6px_6px_0px_#000] transition-transform active:translate-x-[3px] active:translate-y-[3px] active:shadow-none"
        >
          <div className="flex items-center justify-between">
            <span className="grid h-12 w-12 place-items-center rounded-xl border-2 border-white/50 bg-white/15">
              <Wand2 size={22} className="text-white" />
            </span>
            <ChevronRight size={20} className="text-white/80" />
          </div>
          <div>
            <p className="font-serif text-3xl leading-none text-white">Mist Creations</p>
            <p className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.22em] text-teal-50/80">Make photos, videos & posts</p>
          </div>
        </button>

        {/* Stock Library → Misteck Matrix Vault (/gallery) */}
        <button
          onClick={() => { window.location.href = "/gallery"; }}
          data-testid="play-tile-stock-library"
          className="relative flex min-h-[24vh] w-full flex-col justify-between overflow-hidden rounded-[26px] border-2 border-black bg-[#0f172a] p-4 text-left shadow-[6px_6px_0px_#000] transition-transform active:translate-x-[3px] active:translate-y-[3px] active:shadow-none"
        >
          <div className="flex items-center justify-between">
            <span className="grid h-12 w-12 place-items-center rounded-xl border-2 border-cyan-300/60 bg-cyan-300/15">
              <Library size={22} className="text-cyan-200" />
            </span>
            <ChevronRight size={20} className="text-white/80" />
          </div>
          <div>
            <p className="font-serif text-3xl leading-none text-white">Stock Library</p>
            <p className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.22em] text-cyan-100/70">Matrix Vault · graphics · clips · audio</p>
          </div>
        </button>
      </div>
    </div>
  );
}
